import React, { useState, useRef } from 'react';
import axios from 'axios';
import Cookies from 'js-cookie';
import Button from 'shared/ui/Button';
import Modal from 'shared/ui/Modal';
import './ReportModal.css';

const ReportModal = ({ isOpen, onClose, userEmail }) => {
  const [email, setEmail] = useState(userEmail || '');
  const [message, setMessage] = useState('');
  const [image, setImage] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const fileInputRef = useRef(null);

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.match('image.*')) {
        setError("Veuillez sélectionner une image valide");
        return;
      }
      console.log(file.size);
      if (file.size > 500000) {
        setError("La taille de l'image est trop grande.");
        return;
      }

      setImageFile(file);
      setError('');

      const reader = new FileReader();
      reader.onloadend = () => {
        setImage(reader.result.split(',')[1]);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleFileButtonClick = () => {
    fileInputRef.current.click();
  };

  const removeFile = () => {
    setImage(null);
    setImageFile(null);
    fileInputRef.current.value = '';
  };

  // Formater la taille du fichier
  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' octets';
    else if (bytes < 1048576) return (bytes / 1024).toFixed(1) + ' KB';
    else return (bytes / 1048576).toFixed(1) + ' MB';
  };

  const handleSubmit = async () => {
    if (!email.trim() || !message.trim()) {
      setError("Veuillez remplir tous les champs obligatoires.");
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email.trim())) {
      setError("Veuillez entrer une adresse email valide.");
      return;
    }

    setIsLoading(true);
    setError('');
    setSuccess(false);
    const jwt = Cookies.get('user_token');
    try {
      const response = await axios.post(
          'https://unbot.io/api/v1.0/feedback/report',
          {
            email,
            message,
            ...(image ? { image } : {})
          },
          {
            headers: {
              Authorization: `Bearer ${jwt}`,
              'Content-Type': 'application/json'
            }
          }
      );
      if (response.data && response.data.success) {
        setSuccess(true);
        setTimeout(() => {
          onClose();
        }, 1500);
      } else {
        setError("Une erreur est survenue. Veuillez réessayer.");
      }
    } catch (err) {
      setError(err.response?.data?.error?.message || "Erreur lors de l'envoi du signalement.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
      <Modal
          isOpen={isOpen}
          onClose={isLoading ? undefined : onClose}
          title="Signaler un problème"
          overlayClassName="report-modal-overlay"
          panelClassName="report-modal"
          headerClassName="report-modal-header"
          bodyClassName="report-modal-content"
          footerClassName="report-modal-footer"
          closeButtonClassName="report-close-button"
          closeOnOverlayClick={false}
          footer={(
              <>
                <Button variant="secondary" className="report-cancel-button" onClick={onClose} disabled={isLoading}>
                  Annuler
                </Button>
                <Button className="report-save-button" onClick={handleSubmit} disabled={isLoading}>
                  {isLoading ? 'Envoi...' : 'Envoyer'}
                </Button>
              </>
          )}
      >
            <p>Décrivez le problème rencontré. Vous pouvez joindre une capture d'écran (optionnel).</p>
            <div className="report-input-container">
              <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="Votre email"
                  className={error && !email ? 'error' : ''}
                  disabled={isLoading}
              />
              <textarea
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  placeholder="Décrivez le problème..."
                  rows={5}
                  disabled={isLoading}
                  className={error && !message ? 'error' : ''}
              />

              <div className="file-upload-container">
                {!imageFile ? (
                    <div
                        className="file-upload-button"
                        onClick={handleFileButtonClick}
                    >
                      <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><g id="SVGRepo_bgCarrier" strokeWidth="0"></g><g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g><g id="SVGRepo_iconCarrier"> <path d="M17 17H17.01M15.6 14H18C18.9319 14 19.3978 14 19.7654 14.1522C20.2554 14.3552 20.6448 14.7446 20.8478 15.2346C21 15.6022 21 16.0681 21 17C21 17.9319 21 18.3978 20.8478 18.7654C20.6448 19.2554 20.2554 19.6448 19.7654 19.8478C19.3978 20 18.9319 20 18 20H6C5.06812 20 4.60218 20 4.23463 19.8478C3.74458 19.6448 3.35523 19.2554 3.15224 18.7654C3 18.3978 3 17.9319 3 17C3 16.0681 3 15.6022 3.15224 15.2346C3.35523 14.7446 3.74458 14.3552 4.23463 14.1522C4.60218 14 5.06812 14 6 14H8.4M12 15V4M12 4L15 7M12 4L9 7" stroke="#000000" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"></path> </g></svg>
                      Ajouter une capture d'écran
                    </div>
                ) : (
                    <div className="file-preview-container">
                      <img
                          src={`data:image/jpeg;base64,${image}`}
                          alt="Aperçu"
                          className="file-preview-image"
                      />
                      <div className="file-info">
                        <span className="file-name">{imageFile.name}</span>
                        <span className="file-size">{formatFileSize(imageFile.size)}</span>
                      </div>
                      <button
                          type="button"
                          className="remove-file-button"
                          onClick={removeFile}
                          disabled={isLoading}
                      >
                        ×
                      </button>
                    </div>
                )}

                <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    disabled={isLoading}
                    style={{ display: 'none' }}
                />
              </div>

              {error && <p className="report-error-message">{error}</p>}
              {success && <p className="report-success-message">Signalement envoyé avec succès !</p>}
            </div>
      </Modal>
  );
};

export default ReportModal;
